import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router-dom";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Header2 from "../../Component/Header2";

const PendingPaymentsPage = () => {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentData, setPaymentData] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [sortField, setSortField] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");
  const navigate = useNavigate();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [selectedTotalAmount, setSelectedTotalAmount] = useState(0);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotalAmount, setHistoryTotalAmount] = useState(0);
  const [numberOfTransactions, setNumberOfTransactions] = useState(0);

  useEffect(() => {
    const fetchPendingPayments = async () => {
      try {
        const shopkeeperID = localStorage.getItem("shopkeeperID");
        const response = await axios.get(
          `https://sakhisshop.com/api/orders/shop/${shopkeeperID}/all-orders`,
        );

        const mapped = (response.data || []).map((order) => {
          const paid = order.paidAmount || 0;
          const total = order.totalAmount || 0;

          return {
            customerName: order.customerName || "Unknown",
            orderId: order.orderId || "N/A",
            contactNumber: order.contactNumber || "",
            orderDate: order.orderDate
              ? new Date(order.orderDate).toLocaleDateString()
              : "N/A",
            totalAmount: total,
            paidAmount: paid,
            pendingAmount: total - paid, // ✅ calculate pending here
          };
        });

        const onlyPending = mapped.filter((order) => order.pendingAmount > 0);
        setPendingPayments(onlyPending);
      } catch (err) {
        console.error("Error fetching pending payments:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingPayments();
  }, []);

  const handleSave = async (orderId) => {
    const order = pendingPayments.find((o) => o.orderId === orderId);
    if (!order) return;

    if (order.pendingAmount === 0) {
      alert("Payment already done ✅");
      return;
    }

    const paid = paymentData[orderId]?.paid;
    // ✅ Prevent overpayment
    if (Number(paid) > order.pendingAmount) {
      alert(
        `❌ Paid amount cannot be greater than pending amount (₹${order.pendingAmount}).`,
      );
      return;
    }

    if (paid === undefined || paid === "" || Number(paid) <= 0) {
      alert("Please enter a valid paid amount.");
      return;
    }

    try {
      const shopkeeperID = localStorage.getItem("shopkeeperID");

      const response = await axios.put(
        `https://sakhisshop.com/api/orders/shop/${shopkeeperID}/update-payment/${orderId}`,
        { paidAmount: Number(paid) },
      );

      if (response.status === 200) {
        alert("Saved successfully ✅");
        setPendingPayments((prev) =>
          prev.map((order) =>
            order.orderId === orderId
              ? {
                  ...order,
                  paidAmount: order.paidAmount + Number(paid),
                  pendingAmount: order.pendingAmount - Number(paid),
                }
              : order,
          ),
        );

        setPaymentData((prev) => {
          const updated = { ...prev };
          updated[orderId] = { paid: "" };
          return updated;
        });
      } else {
        alert("Failed to save payment. Try again.");
      }
    } catch (err) {
      console.error("Error updating payment data:", err);
      alert("Failed. Something went wrong ❌");
    }
  };
  const handleSaveGroup = async (contactNumber, orders) => {
    const paidValue = Number(paymentData[contactNumber]?.paid);

    if (!paidValue || isNaN(paidValue) || paidValue <= 0) {
      alert("Please enter a valid paid amount.");
      return;
    }

    // Keep your existing shopkeeper ID logic
    const shopkeeperID = localStorage.getItem("shopkeeperID");
    if (!shopkeeperID) {
      alert("Shopkeeper ID not found.");
      return;
    }

    try {
      // ✅ Sort orders by date (oldest first)
      const sortedOrders = [...orders].sort(
        (a, b) => new Date(a.orderDate) - new Date(b.orderDate),
      );

      let remaining = paidValue;

      for (const order of sortedOrders) {
        if (remaining <= 0) break;

        const payForThisOrder = Math.min(order.pendingAmount, remaining);

        // ✅ Send partial or full payment for this order
        await axios.put(
          `https://sakhisshop.com/api/orders/shop/${shopkeeperID}/update-payment/${order.orderId}`,
          { paidAmount: payForThisOrder },
        );

        remaining -= payForThisOrder;
      }

      alert("Payment distributed successfully ✅");

      // ✅ Keep your existing refresh logic
      setPendingPayments((prev) =>
        prev
          .map((order) => {
            if (orders.some((o) => o.orderId === order.orderId)) {
              const matchedOrder = orders.find(
                (o) => o.orderId === order.orderId,
              );
              const payForThisOrder = Math.min(
                matchedOrder.pendingAmount,
                paidValue,
              );
              return {
                ...order,
                paidAmount: order.paidAmount + payForThisOrder,
                pendingAmount: Math.max(
                  order.pendingAmount - payForThisOrder,
                  0,
                ),
              };
            }
            return order;
          })
          .filter((order) => order.pendingAmount > 0),
      );

      // ✅ Reset payment field
      setPaymentData((prev) => ({
        ...prev,
        [contactNumber]: { paid: "" },
      }));
      setTimeout(() => {
        setPendingPayments((prev) => [...prev]);
      }, 0);
    } catch (err) {
      console.error("Error updating grouped payments:", err);
      alert("Something went wrong ❌");
    }
  };

  // const handleViewHistory = (customerName, orderId) => {
  //   // ✅ Open dialog / redirect / fetch API for history
  //   console.log("View history for:", customerName, orderId);
  //   // Example: navigate(/payment-history/${orderId});
  // };
  const handleViewHistory = async (order) => {
    setSelectedOrderId(order.orderId);
    setSelectedCustomerName(order.customerName);
    setSelectedTotalAmount(order.totalAmount);
    setHistoryLoading(true);
    setHistoryOpen(true);

    try {
      const shopkeeperID = localStorage.getItem("shopkeeperID");
      const response = await axios.get(
        `https://sakhisshop.com/api/orders/shop/${shopkeeperID}/payment-history/${order.orderId}`,
      );

      // API should return all payments made for this order
      let history = response.data || [];

      // ✅ Sort by date
      history.sort((a, b) => new Date(a.date) - new Date(b.date));

      // ✅ Calculate pending after each payment
      let runningPending = order.totalAmount; // start with total order amount
      const historyWithPending = history.map((payment) => {
        const paid = payment.paidAmount || 0;
        runningPending -= paid;
        return {
          ...payment,
          pendingAfterPayment: runningPending >= 0 ? runningPending : 0,
        };
      });

      setPaymentHistory(historyWithPending);
      setNumberOfTransactions(historyWithPending.length);

      const totalPaid = historyWithPending.reduce(
        (sum, p) => sum + (p.paidAmount || 0),
        0,
      );
      setHistoryTotalAmount(totalPaid);
    } catch (err) {
      console.error("Error fetching payment history:", err);
      setPaymentHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const filteredPayments = pendingPayments.filter((row) => {
    const nameOrOrderQuery = searchQuery.toLowerCase();
    const contactQuery = contactSearch.toLowerCase();
    return (
      (row.customerName.toLowerCase().includes(nameOrOrderQuery) ||
        row.orderId.toString().toLowerCase().includes(nameOrOrderQuery)) &&
      (row.contactNumber || "").toLowerCase().includes(contactQuery)
    );
  });
  // ✅ Apply sorting on filtered results
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    if (sortField === "All") return 0; // no sorting

    let valA = a[sortField];
    let valB = b[sortField];

    // Convert strings to numbers if needed
    if (typeof valA === "string" && !isNaN(valA)) valA = Number(valA);
    if (typeof valB === "string" && !isNaN(valB)) valB = Number(valB);

    // Special handling for dates
    if (sortField === "orderDate") {
      valA = new Date(a.orderDate);
      valB = new Date(b.orderDate);
    }

    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const filteredPaymentsByNameOrOrder = pendingPayments.filter((row) => {
    const query = searchQuery.toLowerCase();
    return (
      row.customerName.toLowerCase().includes(query) ||
      row.orderId.toString().toLowerCase().includes(query)
    );
  });
  // Filter by contact search
  const filteredByContact = contactSearch
    ? pendingPayments.filter((row) =>
        (row.contactNumber || "")
          .toLowerCase()
          .includes(contactSearch.toLowerCase()),
      )
    : [];

  let groupedArray = [];

  if (contactSearch) {
    const groupedPayments = filteredByContact.reduce((acc, order) => {
      const key = order.contactNumber || "unknown";
      if (!acc[key]) {
        acc[key] = {
          customerName: order.customerName,
          contactNumber: key,
          totalAmount: 0,
          paidAmount: 0,
          pendingAmount: 0,
          orders: [],
          lastOrderId: order.orderId, // store last order ID
          lastOrderDate: order.orderDate, // store last order date
        };
      }
      acc[key].orders.push(order);
      acc[key].totalAmount += order.totalAmount;
      acc[key].paidAmount += order.paidAmount;
      acc[key].pendingAmount += order.pendingAmount;

      const currentOrderDate = new Date(order.orderDate);
      const lastOrderDate = new Date(acc[key].lastOrderDate);
      if (currentOrderDate > lastOrderDate) {
        acc[key].lastOrderId = order.orderId;
        acc[key].lastOrderDate = order.orderDate;
      }
      return acc;
    }, {});

    groupedArray = Object.values(groupedPayments);
  }

  const totalAmountSum = sortedPayments.reduce(
    (acc, row) => acc + row.totalAmount,
    0,
  );

  const paidAmountSum = sortedPayments.reduce(
    (acc, row) =>
      acc + Number(paymentData[row.orderId]?.paid || row.paidAmount || 0),
    0,
  );

  const pendingAmountSum = totalAmountSum - paidAmountSum;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        pb: 8,
        pt: "64px",
      }}
    >
      {/* Header */}
      <Header2 />
      <Container sx={{ mt: 4, mb: 5 }}>
        {/* Page Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bgcolor="#3d5a80"
          color="white"
          borderRadius={1}
          p={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate(-1)} color="warning">
              <ArrowBackIosNewIcon />
            </IconButton>
            <PaymentIcon />
            <Typography
              variant="h6"
              sx={{ color: "white", fontWeight: "bold" }}
            >
              Pending Payments
            </Typography>
          </Box>

          {/* <TextField
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            style: { backgroundColor: "white" },
          }}
          sx={{ width: { xs: "100%", sm: 350 } }}
        /> */}

          <Box display="flex" alignItems="center" gap={2}>
            {/* Search Input */}
            <Box display="flex" alignItems="center" gap={2}>
              {/* Search by Name / Order */}
              <TextField
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Name or Order ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  style: { backgroundColor: "white" },
                }}
                sx={{ width: { xs: "100%", sm: 250 } }}
              />

              {/* Sort By */}
              <FormControl
                variant="outlined"
                size="small"
                sx={{ width: 180, bgcolor: "white" }}
              >
                {/* <InputLabel>Sort By</InputLabel> */}
                <Select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="customerName">Customer Name</MenuItem>

                  <MenuItem value="orderDate">Order Date</MenuItem>
                  <MenuItem value="paidAmount">Paid Amount</MenuItem>
                  <MenuItem value="pendingAmount">Pending Amount</MenuItem>
                </Select>
              </FormControl>

              {/* Sort Order */}
              <FormControl
                variant="outlined"
                size="small"
                sx={{ width: 180, bgcolor: "white" }}
              >
                {/* <InputLabel>Order</InputLabel> */}
                <Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  label="Order"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            bgcolor="#3d5a80"
            color="white"
            borderRadius={1}
            p={2}
            mb={2}
          >
            {/* Header bar content here: title, back button, search by name/order, sort dropdowns */}
          </Box>

          {/* New search box below header */}
          <Box sx={{ mb: 2, mt: 0, px: 2 }}>
            <TextField
              variant="outlined"
              size="small"
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              placeholder="Search by Contact No"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                style: { backgroundColor: "white" },
              }}
              fullWidth
              sx={{ maxWidth: 400 }}
            />
          </Box>
        </Box>

        {/* Table */}
        <Container maxWidth="lg" sx={{ mt: 3 }}>
          {loading ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress />
              <Typography>Loading pending payments...</Typography>
            </Box>
          ) : (
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <TableContainer>
                <Table>
                  {/* Header */}
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>
                        <b>Customer</b>
                      </TableCell>
                      <TableCell>
                        <b>Contact No</b>
                      </TableCell>{" "}
                      {/* New column */}
                      <TableCell>
                        <b>Order ID</b>
                      </TableCell>
                      <TableCell>
                        <b>Date</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Total (₹)</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Paid (₹)</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Pending (₹)</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Action</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  {/* Body */}
                  <TableBody>
                    {contactSearch ? (
                      // Show grouped rows by contact
                      groupedArray.length > 0 ? (
                        groupedArray.map((group, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{group.customerName}</TableCell>
                            <TableCell>{group.contactNumber}</TableCell>
                            <TableCell>{group.lastOrderId}</TableCell>
                            <TableCell>{group.lastOrderDate}</TableCell>

                            <TableCell align="right">
                              ₹{group.totalAmount.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                placeholder="Enter paid amount"
                                value={
                                  paymentData[group.contactNumber]?.paid || ""
                                }
                                onChange={(e) =>
                                  setPaymentData((prev) => ({
                                    ...prev,
                                    [group.contactNumber]: {
                                      ...prev[group.contactNumber],
                                      paid: e.target.value,
                                    },
                                  }))
                                }
                                onClick={() => {
                                  if (group.pendingAmount <= 0) {
                                    alert("Payment already completed ✅");
                                  }
                                }}
                                disabled={group.pendingAmount <= 0}
                                sx={{
                                  width: 120,
                                  backgroundColor:
                                    group.pendingAmount <= 0
                                      ? "#f0f0f0"
                                      : "white",
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              ₹{group.pendingAmount.toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                sx={{ textTransform: "none", mr: 1 }}
                                onClick={() =>
                                  handleSaveGroup(
                                    group.contactNumber,
                                    group.orders,
                                  )
                                }
                              >
                                Save
                              </Button>
                              <IconButton
                                size="small"
                                sx={{ color: "blue" }}
                                onClick={() =>
                                  handleViewHistory(group.orders[0])
                                }
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            align="center"
                            sx={{ py: 3, color: "#888" }}
                          >
                            No customers found ❌
                          </TableCell>
                        </TableRow>
                      )
                    ) : sortedPayments.length > 0 ? (
                      sortedPayments.map((row, idx) => {
                        const paid = Number(
                          paymentData[row.orderId]?.paid || row.paidAmount || 0,
                        );
                        const pending = row.pendingAmount; // backend-updated pending
                        return (
                          <TableRow key={idx} hover>
                            <TableCell>{row.customerName}</TableCell>
                            <TableCell>{row.contactNumber || "N/A"}</TableCell>
                            <TableCell>{row.orderId}</TableCell>
                            <TableCell>{row.orderDate}</TableCell>
                            <TableCell align="right">
                              ₹{row.totalAmount.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={paymentData[row.orderId]?.paid ?? ""}
                                onChange={(e) =>
                                  setPaymentData((prev) => ({
                                    ...prev,
                                    [row.orderId]: { paid: e.target.value },
                                  }))
                                }
                                onClick={() => {
                                  if (row.pendingAmount <= 0) {
                                    alert("Payment already done ✅");
                                  }
                                }}
                                sx={{ width: 80 }}
                                disabled={row.pendingAmount <= 0}
                              />
                            </TableCell>
                            <TableCell align="right">
                              ₹{pending.toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handleSave(row.orderId)}
                                sx={{ textTransform: "none", ml: 1 }}
                              >
                                Save
                              </Button>
                              <IconButton
                                size="small"
                                sx={{ color: "blue" }}
                                onClick={() => handleViewHistory(row)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                          sx={{ py: 3, color: "#888" }}
                        >
                          No pending payments ❌
                        </TableCell>
                      </TableRow>
                    )}

                    {/* ✅ Add Totals row at the bottom for sortedPayments */}
                    {!contactSearch && sortedPayments.length > 0 && (
                      <TableRow
                        sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}
                      >
                        <TableCell>
                          <b>Total</b>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">
                          ₹{totalAmountSum.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          ₹{paidAmountSum.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          ₹{pendingAmountSum.toFixed(2)}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Container>
      </Container>
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Payment History for Order {selectedOrderId}</DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ textAlign: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : paymentHistory.length === 0 ? (
            <Typography>No payment history available.</Typography>
          ) : (
            <>
              <Box sx={{ mb: 2, p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography>
                  <b>Customer:</b> {selectedCustomerName}
                </Typography>
                <Typography>
                  <b>Total Amount:</b> ₹{selectedTotalAmount.toFixed(2)}
                </Typography>
                <Typography>
                  <b>Number of Transactions:</b> {numberOfTransactions}
                </Typography>
                <Typography>
                  <b>Total Paid Amount:</b> ₹{historyTotalAmount.toFixed(2)}
                </Typography>
                <Typography>
                  <b>Pending Amount:</b> ₹
                  {(selectedTotalAmount - historyTotalAmount).toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableRow>
                      <TableCell>
                        <b>Paid Amount (₹)</b>
                      </TableCell>
                      <TableCell>
                        <b>Date</b>
                      </TableCell>
                      <TableCell>
                        <b>Pending After Payment (₹)</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentHistory.map((payment, idx) => (
                      <TableRow key={idx}>
                        <TableCell>₹{payment.paidAmount.toFixed(2)}</TableCell>
                        <TableCell>
                          {payment.date
                            ? new Date(payment.date).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          ₹{payment.pendingAfterPayment.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingPaymentsPage;
